import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useNavigate, useParams } from 'react-router-dom'
import ReactGA from 'react-ga4'

import Loading from '@components/loading'
import CharacteristicCard from '@components/characteristicCard'
import BeneficiaryList from '@components/table/beneficiaryList'
import BeneficiaryFilter from '@components/table/beneficiaryFilter'
import TablePageHandler from '@components/table/tablePageHandler'

import colors from '@settings/styleSettings'
import { LoadingContainer } from './styles'
import {
  getBeneficiariesWithCollection as getBeneficiariesAction,
  getReportToInsurance as getReportToInsuranceAction,
} from '@actions/beneficiary'

const defaultPageSize = 10

const BeneficiaryTable = ({
  beneficiaries,
  beneficiaryTotal,
  beneficiaryFilterState,
  searchBeneficiaries,
  getReportToInsurance,
  onGetReport,
  setBeneficiaryFilterState,
  companyId,
  programmeId,
}) => {
  const [beneficiaryPage, setBeneficiaryPage] = useState(0)
  const [beneficiaryPageSize, setBeneficiaryPageSize] = useState(defaultPageSize)
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState([])
  const [beneficiarySearchTerm, setBeneficiarySearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectAllState, setSelectAllState] = useState()
  const navigate = useNavigate()
  const { insurance } = useParams()

  useEffect(() => {
    if (selectAllState) setSelectedBeneficiaries(beneficiaries)
  }, [selectAllState])

  useEffect(() => {
    if (selectedBeneficiaries?.length === beneficiaries?.length) {
      setSelectAllState(true)
    } else setSelectAllState(false)
  }, [selectedBeneficiaries])

  useEffect(() => {
    setLoading(true)
    searchBeneficiaries(
      beneficiarySearchTerm,
      beneficiaryPage,
      beneficiaryPageSize,
      () => setLoading(false)
    )
  }, [beneficiaryPage])

  useEffect(() => {
    setLoading(true)
    searchBeneficiaries(beneficiarySearchTerm, 0, beneficiaryPageSize, () => {
      setBeneficiaryPage(0)
      setLoading(false)
    })
    setSelectAllState()
  }, [beneficiaryPageSize, beneficiaryFilterState])

  const onSearchBeneficiary = () => {
    searchBeneficiaries(beneficiarySearchTerm, 0, beneficiaryPageSize, () => {
      setBeneficiaryPage(0)
      setLoading(false)
    })
    ReactGA.event({
      category: 'Pesquisa de beneficiário',
      action: 'Clique em pesquisa de beneficiário',
      label: 'Botão de pesquisar beneficiário',
    })

  }

  const handleSelectAllState = (selectState) => {
    setSelectAllState(selectState)
    if (!selectState) setSelectedBeneficiaries([])
  }

  const onBeneficiaryInputChange = (e) => setBeneficiarySearchTerm(e.target.value)

  const onGetReportRelatory = (beneficiaryId) => {
    onGetReport(true)
    getReportToInsurance({ id: beneficiaryId }, getReportCallback)
  }

  const onClickBeneficiary = (beneficiaryId) => {
    onGetReport(true)
    getReportToInsurance({ id: beneficiaryId }, getReportCallback)
  }

  const getReportCallback = async (reportData) => {
    if (reportData) {
      const { id: beneficiaryId } = reportData
      navigate(`/${insurance}/beneficiario/${beneficiaryId}`)
    }
    onGetReport(false)
  }

  const renderLoading = () => (
    <LoadingContainer>
      <Loading color={colors.primary} height={40} width={40} />
    </LoadingContainer>
  )

  return (
    <>
      <CharacteristicCard>
        <BeneficiaryFilter
          filterState={beneficiaryFilterState}
          onSearch={onSearchBeneficiary}
          searchTerm={beneficiarySearchTerm}
          selectAllState={selectAllState}
          onSelectAllChange={handleSelectAllState}
          onInputChange={onBeneficiaryInputChange}
          setFilterState={setBeneficiaryFilterState}
          selectedBeneficiaries={selectedBeneficiaries}
          companyId={companyId}
          programmeId={programmeId}
          labelText={
            (selectAllState
              && `${selectedBeneficiaries?.length} selecionados`)
            || 'Selecionar todos'
          }
        />
        {loading ? (
          renderLoading()
        ) : (
          <BeneficiaryList
            filterState={beneficiaryFilterState}
            setFilterState={setBeneficiaryFilterState}
            beneficiaries={beneficiaries}
            onSelectedBeneficiariesChange={setSelectedBeneficiaries}
            onClickBeneficiary={onClickBeneficiary}
            onGetReportRelatory={onGetReportRelatory}
            selectAll={selectAllState}
            selectedBeneficiaries={selectedBeneficiaries}
          />
        )}
      </CharacteristicCard>
      <TablePageHandler
        sizeOnPage={beneficiaries && beneficiaries?.length}
        page={beneficiaryPage + 1}
        setPage={setBeneficiaryPage}
        pageSize={beneficiaryPageSize}
        setPageSize={setBeneficiaryPageSize}
        total={beneficiaryTotal}
      />
    </>
  )
}

BeneficiaryTable.propTypes = {
  searchBeneficiaries: PropTypes.func,
  getReportToInsurance: PropTypes.func,
  beneficiaryTotal: PropTypes.number,
  beneficiaries: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      age: PropTypes.number,
      insuranceNumber: PropTypes.string,
      cpf: PropTypes.string,
      sex: PropTypes.string,
      birthdate: PropTypes.string,
      lastGeneralCollection: PropTypes.shape({
        collectedAt: PropTypes.string,
        height: PropTypes.number,
        weight: PropTypes.number,
        abdominalCircumference: PropTypes.number,
        waistCircumference: PropTypes.number,
        hipCircumference: PropTypes.number,
        fat: PropTypes.number,
        visceralFat: PropTypes.number,
        leanMass: PropTypes.number,
        calories: PropTypes.number,
        triglycerides: PropTypes.number,
        glucose: PropTypes.number,
        hdlCholesterol: PropTypes.number,
        ldlCholesterol: PropTypes.number,
        systBloodPressure: PropTypes.number,
        diastBloodPressure: PropTypes.number,
        totalCholesterol: PropTypes.number,
        notHdl: PropTypes.number,
        hasSystemicArterialHypertension: PropTypes.bool,
        hasDiabetes: PropTypes.bool,
      }),
    })
  ),
  beneficiaryFilterState: PropTypes.shape({
    noRisk: PropTypes.bool,
    highRisk: PropTypes.bool,
    mediumRisk: PropTypes.bool,
    lowRisk: PropTypes.bool,
    diabetic: PropTypes.bool,
    hypertensive: PropTypes.bool,
    overweight: PropTypes.bool,
    dyslipidemic: PropTypes.bool,
    activeAccounts: PropTypes.bool,
    frequentAccessApp: PropTypes.bool,
  }),
  onGetReport: PropTypes.func,
  setBeneficiaryFilterState: PropTypes.func.isRequired,
  companyId: PropTypes.number,
  programmeId: PropTypes.number,
}

BeneficiaryTable.defaultProps = {
  beneficiaryTotal: 0,
  beneficiaries: [],
  searchBeneficiaries: () => {},
  getReportToInsurance: () => {},
  beneficiaryFilterState: {
    noRisk: false,
    highRisk: false,
    mediumRisk: false,
    lowRisk: false,
    diabetic: false,
    hypertensive: false,
    overweight: false,
    dyslipidemic: false,
    activeAccounts: false,
    frequentAccessApp: false,
  },
  onGetReport: () => {},
  companyId: undefined,
  programmeId: undefined,
}

const mapStateToProps = ({ beneficiary }) => ({
  beneficiaries: beneficiary.beneficiaries,
  beneficiaryTotal: beneficiary.total,
  getReportToInsurance: getReportToInsuranceAction,
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    getBeneficiariesWithCollection: getBeneficiariesAction,
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(BeneficiaryTable)
